import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "ve_cookie_consent";

export const getConsent = () => {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
};

/** Bandeau de consentement cookies RGPD : refuser est aussi simple qu'accepter. */
export function CookieBanner() {
  const [consent, setConsent] = useState(getConsent());
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const reopen = () => { setConsent(null); setCustom(true); };
    window.addEventListener("ve-open-cookies", reopen);
    return () => window.removeEventListener("ve-open-cookies", reopen);
  }, []);

  if (consent) return null;

  const save = (c) => {
    localStorage.setItem(KEY, JSON.stringify({ ...c, ts: new Date().toISOString() }));
    setConsent(c);
    window.dispatchEvent(new Event("ve-consent-changed"));
  };

  return (
    <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6">
      <div className="max-w-3xl mx-auto night rounded-3xl border border-white/15 shadow-2xl p-6 md:p-7">
        <div className="flex items-start gap-4">
          <Cookie className="w-6 h-6 text-goldsoft shrink-0 mt-0.5" strokeWidth={1.6} />
          <div className="flex-1">
            <p className="font-serif text-lg text-[#f2f0ff] mb-1.5">Nous respectons votre vie privée</p>
            <p className="text-muted-night text-sm leading-relaxed">
              Nous utilisons des cookies strictement nécessaires au fonctionnement du site (connexion, sécurité) et,
              avec votre accord, des cookies de mesure d'audience. Vous pouvez accepter, refuser ou personnaliser —
              votre choix est modifiable à tout moment sur la page <Link to="/cookies" className="text-goldsoft hover:underline">Cookies</Link>.
            </p>
            {custom && (
              <div className="mt-4 space-y-3" data-testid="cookie-custom-panel">
                <label className="flex items-center justify-between gap-4 text-sm text-[#e9e6ff]">
                  <span>Cookies nécessaires <span className="text-muted-night text-xs">(toujours actifs)</span></span>
                  <input type="checkbox" checked disabled className="w-4 h-4 accent-[#d4af37] opacity-60" data-testid="cookie-necessary" />
                </label>
                <label className="flex items-center justify-between gap-4 text-sm text-[#e9e6ff] cursor-pointer">
                  <span>Mesure d'audience <span className="text-muted-night text-xs">(statistiques anonymes)</span></span>
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="w-4 h-4 accent-[#d4af37]" data-testid="cookie-analytics" />
                </label>
                <label className="flex items-center justify-between gap-4 text-sm text-[#e9e6ff] cursor-pointer">
                  <span>Marketing <span className="text-muted-night text-xs">(contenus personnalisés)</span></span>
                  <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="w-4 h-4 accent-[#d4af37]" data-testid="cookie-marketing" />
                </label>
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-2.5">
              <button onClick={() => save({ necessary: true, analytics: false, marketing: false })} data-testid="cookie-refuse"
                className="px-5 py-2.5 rounded-full text-xs font-medium border border-white/25 text-[#e9e6ff] hover:border-goldsoft transition-colors">Tout refuser</button>
              {custom ? (
                <button onClick={() => save({ necessary: true, analytics, marketing })} data-testid="cookie-save"
                  className="px-5 py-2.5 rounded-full text-xs font-medium border border-white/25 text-[#e9e6ff] hover:border-goldsoft transition-colors">Enregistrer mes choix</button>
              ) : (
                <button onClick={() => setCustom(true)} data-testid="cookie-customize"
                  className="px-5 py-2.5 rounded-full text-xs font-medium border border-white/25 text-[#e9e6ff] hover:border-goldsoft transition-colors">Personnaliser</button>
              )}
              <button onClick={() => save({ necessary: true, analytics: true, marketing: true })} data-testid="cookie-accept"
                className="btn-gold px-5 py-2.5 rounded-full text-xs font-medium">Tout accepter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
